import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
  phoneNumberValidator,
  responsable,
} from 'src/app/directives/patient-validator.directive';
import { Civilite } from 'src/app/interfaces/civilite';
import { Client } from 'src/app/interfaces/client';
import { UserAdmin } from 'src/app/interfaces/user-admin';
import { CiviliteService } from 'src/app/services/api/civilite.service';
import { SuperAdminService } from 'src/app/services/api/super-admin.service';
import { ErrorService } from 'src/app/services/error.service';

@Component({
  selector: 'app-ps-info',
  templateUrl: './ps-info.component.html',
  styleUrls: ['./ps-info.component.scss'],
})
export class PsInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() ps!: UserAdmin;

  public psUpdateForm!: FormGroup;

  @Input() clients: Client[] = [];

  public clientMultiCtrl: FormControl = new FormControl('', [
    Validators.required,
  ]);

  /** control for the MatSelect filter keyword multi-selection */
  public clientMultiFilterCtrl: FormControl = new FormControl();

  /** list of banks filtered by search keyword */
  public filteredClientsMulti: ReplaySubject<Client[]> = new ReplaySubject<
    Client[]
  >(1);

  //@ts-ignore
  @ViewChild('multiSelect', { static: true }) multiSelect: MatSelect;

  /** Subject that emits when the component has been destroyed. */
  protected _onDestroy = new Subject<void>();

  public errorMessage: string = '';
  public errorType: string = '';

  civiliteTab: Civilite[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private civiliteService: CiviliteService,
    private errorService: ErrorService,
    public route: Router,
    public superAdminService: SuperAdminService
  ) {}

  ngOnInit(): void {
    this.getCivilite();

    this.psUpdateForm = new FormGroup({
      lname: new FormControl(this.ps.lname),
      fname: new FormControl(this.ps.fname),
      phoneNumber: new FormControl(this.ps.phoneNumber, [
        phoneNumberValidator(),
      ]),
      clients: this.clientMultiCtrl,
      civilite: new FormControl(this.ps.civilite),
      responsable: new FormControl(this.ps.responsable ? 'true' : 'false', [
        Validators.required,
      ]),
      res_email: new FormControl(this.ps.responsable?.res_email || '', [
        responsable(),
        Validators.pattern(
          '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
        ),
      ]),
      res_identifiant: new FormControl(
        this.ps.responsable?.res_identifiant || '',
        [responsable()]
      ),
      res_description: new FormControl(
        this.ps.responsable?.res_description || '',
        [responsable()]
      ),
    });

    this.psUpdateForm.get('responsable')?.valueChanges.subscribe((value) => {
      const isResponsable = value === 'true';
      this.toggleResponsableValidators(isResponsable);
    });

    this.clientMultiCtrl.setValue(this.ps.clients);

    this.filteredClientsMulti.next(this.clients.slice());
    this.clientMultiFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterClientsMulti();
      });
  }

  toggleResponsableValidators(isResponsable: boolean): void {
    const emailControl = this.psUpdateForm.get('res_email');
    const identifiantControl = this.psUpdateForm.get('res_identifiant');
    const descriptionControl = this.psUpdateForm.get('res_description');

    if (isResponsable) {
      emailControl?.setValidators([
        Validators.required,
        Validators.pattern(
          '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
        ),
      ]);
      identifiantControl?.setValidators([Validators.required]);
      descriptionControl?.setValidators([Validators.required]);
    } else {
      emailControl?.clearValidators();
      identifiantControl?.clearValidators();
      descriptionControl?.clearValidators();
    }

    emailControl?.updateValueAndValidity();
    identifiantControl?.updateValueAndValidity();
    descriptionControl?.updateValueAndValidity();
  }

  ngAfterViewInit() {
    this.setInitialValue();
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private getCivilite() {
    this.civiliteService.getCivilite().subscribe(
      (res) => {
        if (res.result == 'SUCCESS') {
          this.civiliteTab = res.content;
        } else {
          this.errorService.errorType = res.result;
          this.errorService.errorMessage = res.content;
          this.route.navigate(['/error']);
        }
      },
      (resError) => {
        this.errorService.errorType = 'ERROR';
        this.errorService.errorMessage = resError.error.detail;
        this.route.navigate(['/error']);
      }
    );
  }

  protected setInitialValue() {
    this.filteredClientsMulti
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.multiSelect.compareWith = (a: Client, b: Client) =>
          a && b && a.id === b.id;
      });
  }

  protected filterClientsMulti() {
    if (!this.clients) {
      return;
    }
    // get the search keyword
    let search = this.clientMultiFilterCtrl.value;
    if (!search) {
      this.filteredClientsMulti.next(this.clients.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    // filter the banks
    this.filteredClientsMulti.next(
      this.clients.filter(
        (client) => client.name.toLowerCase().indexOf(search) > -1
      )
    );
  }

  public hasError = (controlName: string, errorName: string) => {
    return this.psUpdateForm?.controls[controlName].hasError(errorName);
  };

  updatePS(formValue: any) {
    formValue.id = this.ps.id;
    formValue.email = this.ps.email;
    this.superAdminService.updatePS(formValue).subscribe((res) => {
      if (res.result == 'SUCCESS') {
        this.updatePSInfo(formValue);
      }
      this.errorMessage = res.content;
      this.errorType = res.result;
    });
  }

  private updatePSInfo(updatedInfo: any) {
    if (this.ps.lname != updatedInfo.lname) {
      this.ps.lname = updatedInfo.lname;
    }

    if (this.ps.fname != updatedInfo.fname) {
      this.ps.fname = updatedInfo.fname;
    }

    if (this.ps.phoneNumber != updatedInfo.phoneNumber) {
      this.ps.phoneNumber = updatedInfo.phoneNumber;
    }
  }
}
